import React, { FC, memo, useEffect } from "react"
import { BookerPaymentMethodInput, GetCompanyQuery, useGetCompanySettingsPaymentmethodsQuery } from "../../generated/graphql"
import { FormikProps } from "formik"
import { RegistrationPanelValidationType } from "./RegistrationPanelValidationSchema"
import { CompanyMethods } from "../../views/PaymentMethodTable/PaymentMethodTable"
import { Button } from "antd"
import { EditOutlined } from "@ant-design/icons"
import css from "./SepaData.module.css"
import { Link, useLocation } from "react-router-dom"
import {
    // URL_REGISTER_SEPA_CREATE,
    // URL_REGISTER_SEPA_CREATE_BOOKERID,
    URL_REGISTER_SEPA_EDIT_ID,
} from "../../models/url"
import SepaDataCreate from "./SepaDataCreate"
import { margin } from "../../styles/layout"
import { colorDeepDarkGrey } from "../../styles/colors"
import { red } from "@ant-design/colors"
import { Maybe } from "graphql/jsutils/Maybe"
import { sanatizeErrors } from "./loginRegisterHelpers"

type SepaDataProps = {
    formikProps: FormikProps<RegistrationPanelValidationType>
    companySettings?: GetCompanyQuery["companyPublic"]
    useCase?: "registration" | "account"
}

const SepaData: FC<SepaDataProps> = ({ formikProps, useCase }) => {
    const [creatingNewSepa, setCreatingNewSepa] = React.useState(false)
    const location = useLocation()
    const paymentMethods = formikProps.values?.paymentMethods
    // const sepaPaymentMethods = paymentMethods?.filter((pm) => pm.sepaAccountholder)
    const sepaPaymentMethods = paymentMethods?.filter((pm) => pm.type === "SepaDebit") ?? []
    const bookerId = formikProps.values?.id

    const sanatizedPaymentMethodErrors = sanatizeErrors(formikProps).sanatizedPaymentMethodErrors
    const hasPaymentMethodErrors = sanatizeErrors(formikProps).hasPaymentMethodErrors

    // const allPaymentMethods = formikProps?.values?.paymentMethods ?? []
    const sanatizeSepaPaymentMethods = () => {
        const sanatizedPaymentMethods: Array<BookerPaymentMethodInput> = []
        const valueIsEmpty = (value?: Maybe<string> | undefined) => value === "" || value === undefined || value === null

        sepaPaymentMethods.forEach((pm) => {
            if (pm.type === "SepaDebit" && valueIsEmpty(pm.sepaAccountholder)) {
                return
            } else {
                sanatizedPaymentMethods.push(pm)
            }
        })

        return sanatizedPaymentMethods
    }

    const sanatizedPaymentMethods = sanatizeSepaPaymentMethods() ?? []

    useEffect(() => {
        if (sanatizedPaymentMethods?.length) {
            formikProps.validateField("paymentMethods")
        }
    }, [])

    const { data: companyPaymentMethods, error: companyPaymentMethodsError, loading: companyPaymentMethodsLoading } = useGetCompanySettingsPaymentmethodsQuery({ fetchPolicy: "no-cache" }) // no cache because we want to refetch the data if there is an error
    const companyMethods: CompanyMethods | undefined = companyPaymentMethods?.settings

    if (!companyMethods?.sepaDebit) return null

    const renderSepaData = () => {
        const SepaDataRender = ({ data, hasError }: { data: BookerPaymentMethodInput; hasError: boolean }) => {
            const shortenedIban = data.iban ? `${data.iban.slice(0, 4)}...${data.iban.slice(-4)}` : ""
            const shortenedBankAndBic = data.bank ? `${data.bank.slice(0, 20)}... ${data.bic}` : ""
            const editUrl = URL_REGISTER_SEPA_EDIT_ID.replace(":id", data.id).replace(":bookerId", bookerId)

            return (
                <div
                    className={css.sepaData}
                    style={{
                        border: hasError ? "2px solid red" : "none",
                        padding: "8px",
                        marginBottom: "8px",
                    }}
                >
                    <div>
                        <div>{`${data.sepaAccountholder} ${shortenedIban}`}</div>
                        <div>{shortenedBankAndBic}</div>
                    </div>
                    {useCase === "account" && (
                        <Link
                            to={{
                                pathname: editUrl,
                                state: { fromUrl: { pathname: location.pathname }, bookerId },
                            }}
                        >
                            <Button icon={<EditOutlined />} type='link' />
                        </Link>
                    )}
                </div>
            )
        }

        // if (hasPaymentMethodErrors || creatingNewSepa) {
        if (hasPaymentMethodErrors || creatingNewSepa || sanatizedPaymentMethods.length === 0) {
            // Blockierende Fehler gehören immer zur gerade angelegten (letzten) Methode: Formular offen
            // halten, damit der Fehler sichtbar und korrigierbar bleibt (B-82808).
            return <SepaDataCreate formikProps={formikProps} useCase={useCase} creatingNewSepa={creatingNewSepa || hasPaymentMethodErrors} setCreatingNewSepa={setCreatingNewSepa} />
        }

        const paymentMethodErrors = Array.isArray(formikProps?.errors?.paymentMethods) ? formikProps?.errors?.paymentMethods : []

        return (
            <div>
                {sanatizedPaymentMethods.map((pm) => {
                    const originalIndex = formikProps.values.paymentMethods?.findIndex((method) => method.id === pm.id) ?? -1

                    const hasError =
                        originalIndex !== -1 &&
                        paymentMethodErrors[originalIndex] &&
                        typeof paymentMethodErrors[originalIndex] === "object" &&
                        Object.keys(paymentMethodErrors[originalIndex] || {}).length > 0

                    return <SepaDataRender key={pm.id} data={pm} hasError={hasError} />
                })}
            </div>
        )
    }

    if (companyPaymentMethodsLoading) return <div>Loading...</div>
    if (companyPaymentMethodsError) return <div>Error</div>

    return (
        <div style={{ textAlign: "left" }} data-testid='sepa-data' id='sepa-data'>
            <h3 style={{ marginBottom: 0, marginLeft: margin.xs, color: colorDeepDarkGrey }}>Lastschriftdaten</h3>
            <div className={css.sepaDataContainer} style={{ borderColor: hasPaymentMethodErrors ? red[4] : undefined }}>
                {renderSepaData()}
            </div>
        </div>
    )
}

export default memo(SepaData)
